import readXlsxFile, { readSheetNames } from "read-excel-file";

/** Pré-visualização de planilhas (xlsx/csv) no próprio navegador: só lê valores, nunca executa macros nem fórmulas. */

export const PREVIEW_MAX_BYTES = 10 * 1024 * 1024;
export const PREVIEW_MAX_ROWS = 200;
export const PREVIEW_MAX_COLS = 50;
const PREVIEW_MAX_SHEETS = 30;
const PREVIEW_MAX_CELL_CHARS = 500;

export type SpreadsheetKind = "xlsx" | "csv";

export interface PreviewSheet {
  name: string;
  rows: string[][];
  totalRows: number;
  totalCols: number;
  truncatedRows: boolean;
  truncatedCols: boolean;
}

export interface SpreadsheetPreview {
  sheets: PreviewSheet[];
  sheetsTruncated: boolean;
}

/** Decide se o anexo tem pré-visualização. `.xls` (binário antigo) não é suportado. */
export function spreadsheetKind(a: {
  name?: string;
  mimeType?: string;
}): SpreadsheetKind | null {
  const name = (a.name || "").split(";")[0].trim().toLowerCase();
  const mime = (a.mimeType || "").split(";")[0].trim().toLowerCase();
  if (
    name.endsWith(".xlsx") ||
    mime === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
  )
    return "xlsx";
  if (name.endsWith(".csv") || mime === "text/csv") return "csv";
  return null;
}

export function cellToText(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s: string;
  if (v instanceof Date) s = Number.isNaN(v.getTime()) ? "" : formatDate(v);
  else s = String(v);
  return s.length > PREVIEW_MAX_CELL_CHARS
    ? `${s.slice(0, PREVIEW_MAX_CELL_CHARS)}…`
    : s;
}

function formatDate(d: Date): string {
  const iso = d.toISOString();
  return iso.endsWith("T00:00:00.000Z")
    ? iso.slice(0, 10)
    : `${iso.slice(0, 10)} ${iso.slice(11, 19)}`;
}

/** Aplica os limites de linhas/colunas e converte tudo para texto (a tela renderiza como texto, sem HTML). */
export function buildSheet(name: string, data: unknown[][]): PreviewSheet {
  const totalRows = data.length;
  const totalCols = data.reduce<number>((m, r) => Math.max(m, r.length), 0);
  const cols = Math.min(totalCols, PREVIEW_MAX_COLS);
  const rows = data.slice(0, PREVIEW_MAX_ROWS).map((r) => {
    const out: string[] = [];
    for (let c = 0; c < cols; c++) out.push(cellToText(r[c]));
    return out;
  });
  return {
    name,
    rows,
    totalRows,
    totalCols,
    truncatedRows: totalRows > PREVIEW_MAX_ROWS,
    truncatedCols: totalCols > PREVIEW_MAX_COLS,
  };
}

/** Detecta o separador (`,`, `;` ou tab) contando só fora de aspas, na 1ª linha lógica. Empate ou nenhum: vírgula. */
export function detectDelimiter(src: string): string {
  const counts: Record<string, number> = { ",": 0, ";": 0, "\t": 0 };
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (ch === '"') inQuotes = !inQuotes;
    else if (!inQuotes) {
      if (ch === "\n" || ch === "\r") break;
      if (ch in counts) counts[ch]++;
    }
  }
  let best = ",";
  for (const d of [";", "\t"]) if (counts[d] > counts[best]) best = d;
  return best;
}

/** Decodifica o CSV como UTF-8 e, se houver bytes inválidos, como Windows-1252 (Latin-1 do Excel BR). */
export function decodeCsv(buffer: ArrayBuffer): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder("windows-1252").decode(buffer);
  }
}

/** CSV simples (RFC 4180): aspas, aspas duplicadas, quebras de linha dentro de campo; separador `,`, `;` ou tab detectado na 1ª linha. */
export function parseCsv(text: string): string[][] {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delim = detectDelimiter(src);
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  for (let i = 0; i < src.length; i++) {
    const ch = src[i];
    if (inQuotes) {
      if (ch === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i++;
        } else inQuotes = false;
      } else field += ch;
    } else if (ch === '"') inQuotes = true;
    else if (ch === delim) {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && src[i + 1] === "\n") i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else field += ch;
  }
  if (field !== "" || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows;
}

export class SpreadsheetPreviewError extends Error {}

export async function loadSpreadsheetPreview(
  buffer: ArrayBuffer,
  kind: SpreadsheetKind,
  fileName = "Planilha"
): Promise<SpreadsheetPreview> {
  if (buffer.byteLength === 0)
    throw new SpreadsheetPreviewError("O arquivo está vazio.");
  if (buffer.byteLength > PREVIEW_MAX_BYTES) {
    throw new SpreadsheetPreviewError(
      `O arquivo passa do limite de ${PREVIEW_MAX_BYTES / (1024 * 1024)} MB para visualização.`
    );
  }
  if (kind === "csv") {
    const text = decodeCsv(buffer);
    return {
      sheets: [buildSheet(fileName, parseCsv(text))],
      sheetsTruncated: false,
    };
  }
  try {
    const names = await readSheetNames(buffer);
    if (names.length === 0) throw new Error("sem abas");
    const used = names.slice(0, PREVIEW_MAX_SHEETS);
    const sheets: PreviewSheet[] = [];
    for (const name of used) {
      const data = await readXlsxFile(buffer, { sheet: name });
      sheets.push(buildSheet(name, data as unknown[][]));
    }
    return { sheets, sheetsTruncated: names.length > used.length };
  } catch {
    throw new SpreadsheetPreviewError(
      "Não foi possível ler a planilha: o arquivo parece corrompido ou inválido."
    );
  }
}
